import { FlexCol, FlexRow } from '@/components/common/Flex';
import { Text } from '@/components/common/Typography';
import {
    BudgetProgressBarProps,
    OVERAGE_COLOR,
    useBudgetProgressBarProps,
} from '@common/react/budget';
import styleguide from '@common/styleguide';
import { toDollars } from '@common/util/currency';
import chroma from 'chroma-js';

const HEIGHT = 12;
const DIVIDER_EXTRA_HEIGHT = 4;

export const BudgetProgressBar = ({
    incomeAmount,
    currentAmount,
    otherAmount,
    budgetPurpose,
}: BudgetProgressBarProps) => {
    const {
        otherPercentage,
        currentPercentage,
        totalPercentage,
        remainingAmount,
        color,
    } = useBudgetProgressBarProps({
        incomeAmount,
        otherAmount,
        currentAmount,
        budgetPurpose,
    });

    return (
        <FlexCol w="100%" gap={4}>
            <FlexRow rounded={6} style={{ height: HEIGHT }} bg="gray.200">
                {otherPercentage > 0 && (
                    <>
                        <FlexCol
                            h="full"
                            style={{
                                width: `${otherPercentage}%`,
                                backgroundColor: styleguide.colors.gray,
                            }}
                            roundedTopLeft={6}
                            roundedBottomLeft={6}
                        />
                        <FlexCol
                            w="3px"
                            style={{
                                height: HEIGHT + DIVIDER_EXTRA_HEIGHT,
                                marginTop: -(DIVIDER_EXTRA_HEIGHT / 2),
                                backgroundColor: styleguide.colors.grayDark,
                            }}
                            rounded={4}
                        />
                    </>
                )}

                {currentPercentage > 0 && (
                    <>
                        <FlexCol
                            h="full"
                            style={{
                                width: `${currentPercentage}%`,
                                backgroundColor:
                                    totalPercentage === 100
                                        ? OVERAGE_COLOR
                                        : color,
                            }}
                            {...(otherPercentage === 0 && {
                                roundedTopLeft: 6,
                                roundedBottomLeft: 6,
                            })}
                            {...(totalPercentage === 100 && {
                                roundedTopRight: 6,
                                roundedBottomRight: 6,
                            })}
                        />

                        {totalPercentage < 100 && (
                            <FlexCol
                                w="3px"
                                style={{
                                    height: HEIGHT + DIVIDER_EXTRA_HEIGHT,
                                    marginTop: -(DIVIDER_EXTRA_HEIGHT / 2),
                                    backgroundColor: chroma(color)
                                        .darken(1)
                                        .hex(),
                                }}
                                rounded={4}
                            />
                        )}
                    </>
                )}
            </FlexRow>
            <FlexRow justify="flex-end">
                <Text>
                    {remainingAmount >= 0
                        ? `${toDollars(remainingAmount)} left`
                        : `${toDollars(-remainingAmount)} over`}
                </Text>
            </FlexRow>
        </FlexCol>
    );
};
